import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";

// V1.65 · Alta rápida desde cualquier desplegable: el mismo formulario de
// Configuración/Contabilidad, sin salir del formulario en curso.
const todayIso = () => new Date().toISOString().slice(0, 10);

export const QUICK_ADD_VALUE = "__quick_add__";

export const QUICK_ADD_ENTITIES = {
  location: {
    title: "Nueva ubicación",
    endpoint: "/locations",
    fields: [{ name: "name", label: "Nombre de la ubicación", required: true, placeholder: "Ej: Metro Ventas" }],
  },
  circuit: {
    title: "Nueva ruta / circuito",
    endpoint: "/circuits",
    fields: [{ name: "name", label: "Nombre de la ruta", required: true, placeholder: "Ej: MÓSTOLES" }],
  },
  vehicle: {
    title: "Nuevo vehículo",
    endpoint: "/vehicles",
    fields: [
      { name: "plate", label: "Matrícula", required: true, placeholder: "1234 ABC", mono: true },
      { name: "brand", label: "Marca", placeholder: "Seat" },
      { name: "model", label: "Modelo", placeholder: "Ibiza" },
      { name: "max_students", label: "Plazas de alumno", type: "number", default: 2 },
    ],
  },
  teacher: {
    title: "Nuevo profesor",
    endpoint: "/teachers",
    fields: [
      { name: "name", label: "Nombre y apellidos", required: true, placeholder: "José Alfredo Pérez" },
      { name: "phone", label: "Teléfono", placeholder: "600000000" },
      { name: "email", label: "Email", type: "email", placeholder: "profe@autoescuela.es" },
    ],
  },
  tasa: {
    title: "Alta de tasa DGT",
    endpoint: "/dgt-tasas",
    hint: "Caducidad automática: compra + 6 meses (editable)",
    fields: [
      { name: "number", label: "Nº tasa", required: true, placeholder: "481719404681", mono: true },
      {
        name: "tipo", label: "Tipo", type: "select", default: "2.1",
        options: [
          { value: "2.1", label: "2.1 · Nueva Admisión (2T)" },
          { value: "4.1", label: "4.1 · Traslado (4N)" },
        ],
      },
      { name: "purchase_date", label: "Fecha de compra", type: "date", default: todayIso },
      { name: "expiry_date", label: "Caducidad (vacío = +6 meses)", type: "date" },
    ],
  },
};

const initialValues = (fields) => {
  const v = {};
  for (const f of fields) {
    const d = typeof f.default === "function" ? f.default() : f.default;
    v[f.name] = d === undefined ? "" : d;
  }
  return v;
};

export default function QuickAddDialog({ open, entity, extra, onClose, onCreated }) {
  const cfg = QUICK_ADD_ENTITIES[entity];
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && cfg) setValues(initialValues(cfg.fields));
  }, [open, entity]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return null;

  const submit = async (e) => {
    e.preventDefault();
    const missing = cfg.fields.find((f) => f.required && !String(values[f.name] ?? "").trim());
    if (missing) {
      toast.error(`Falta ${missing.label.toLowerCase()}`);
      return;
    }
    const payload = { ...extra };
    for (const f of cfg.fields) {
      const raw = values[f.name];
      payload[f.name] = f.type === "number" ? Number(raw) || 1 : String(raw ?? "").trim();
    }
    setSaving(true);
    try {
      const { data } = await api.post(cfg.endpoint, payload);
      toast.success(`${cfg.title.replace(/^(Nueva|Nuevo|Alta de)\s/i, "")} añadida correctamente`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid={`quick-add-dialog-${entity}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4 text-orange-600" /> {cfg.title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {cfg.hint && <p className="text-[11px] text-neutral-500 italic">{cfg.hint}</p>}
          {cfg.fields.map((f) => (
            <div key={f.name}>
              <label className="text-xs font-semibold text-neutral-600 mb-1 block">
                {f.label} {f.required && <span className="text-orange-600">*</span>}
              </label>
              {f.type === "select" ? (
                <select
                  value={values[f.name] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                  data-testid={`quick-add-${entity}-${f.name}`}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type || "text"}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                  placeholder={f.placeholder || ""}
                  data-testid={`quick-add-${entity}-${f.name}`}
                  className={
                    "w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 " +
                    (f.mono ? "font-mono" : "")
                  }
                />
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid={`quick-add-${entity}-cancel`}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid={`quick-add-${entity}-submit`}
              className="btn-primary flex-1"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Guardar y usar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
